import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { InstallPermissionAccessTypes } from '../constants/installPermissions';

// Produces a new Set by adding entries where delta[id] is true and removing entries where delta[id] is false.
import { jsx as _jsx } from "react/jsx-runtime";
function applyDeltaToSet(base, delta) {
  const result = new Set(base);
  Object.entries(delta).forEach(([idStr, isSelected]) => {
    const id = Number(idStr);
    if (isSelected) {
      result.add(id);
    } else {
      result.delete(id);
    }
  });
  return result;
}
const InstallPermissionsContext = /*#__PURE__*/createContext(null);
export function InstallPermissionsProvider({
  children,
  initialInstallPermissionAccessState = InstallPermissionAccessTypes.SUPER_ADMINS_AND_AUTHORIZED_INSTALLERS,
  entityTypeAndIds = []
}) {
  const [initialUserIds, setInitialUserIds] = useState(() => entityTypeAndIds.reduce((acc, entity) => {
    if (entity.entityType === 'USER') {
      acc.add(entity.entityId);
    }
    return acc;
  }, new Set()));
  const [initialTeamIds, setInitialTeamIds] = useState(() => entityTypeAndIds.reduce((acc, entity) => {
    if (entity.entityType === 'TEAM') {
      acc.add(entity.entityId);
    }
    return acc;
  }, new Set()));
  const [installPermissionAccessState, setInstallPermissionAccessState] = useState(initialInstallPermissionAccessState);
  const [updatedUsers, setUpdatedUsers] = useState({});
  const [updatedTeams, setUpdatedTeams] = useState({});
  const previousPermissionStateRef = useRef(installPermissionAccessState);

  // Keeps track of previous state to understand when the permission type changes
  useEffect(() => {
    const previousPermissionState = previousPermissionStateRef.current;
    const isNewPermissionState = previousPermissionState !== installPermissionAccessState;

    // Clear updated users when permission type changes to maintain consistent state
    if (isNewPermissionState) {
      const changingFromSpecificUsersAndTeams = previousPermissionState === InstallPermissionAccessTypes.SPECIFIC_USERS_AND_TEAMS;
      // If we're changing from SPECIFIC_USERS_AND_TEAMS to a different permission state, we need to clear the updated users
      if (changingFromSpecificUsersAndTeams) {
        setUpdatedUsers({});
        setUpdatedTeams({});
      }
    }
    previousPermissionStateRef.current = installPermissionAccessState;
  }, [installPermissionAccessState]);
  const resetUpdatedUsers = useCallback(() => {
    setUpdatedUsers({});
    setUpdatedTeams({});
    setInstallPermissionAccessState(initialInstallPermissionAccessState);
  }, [initialInstallPermissionAccessState]);
  const commitUserUpdates = useCallback(() => {
    setInitialUserIds(applyDeltaToSet(initialUserIds, updatedUsers));
    setUpdatedUsers({});
  }, [initialUserIds, updatedUsers]);
  const commitTeamUpdates = useCallback(() => {
    setInitialTeamIds(applyDeltaToSet(initialTeamIds, updatedTeams));
    setUpdatedTeams({});
  }, [initialTeamIds, updatedTeams]);
  const getSelectedEntityTypeAndIds = useCallback(() => {
    let selectedEntityTypeAndIds = [];
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (isSpecificUsersAndTeams) {
      const selectedUserIds = applyDeltaToSet(initialUserIds, updatedUsers);
      const selectedTeamIds = applyDeltaToSet(initialTeamIds, updatedTeams);
      selectedEntityTypeAndIds = [...Array.from(selectedUserIds, entityId => ({
        entityType: 'USER',
        entityId
      })), ...Array.from(selectedTeamIds, entityId => ({
        entityType: 'TEAM',
        entityId
      }))];
    }
    return selectedEntityTypeAndIds;
  }, [initialUserIds, updatedUsers, installPermissionAccessState, initialTeamIds, updatedTeams]);
  const updatedUsersCount = useMemo(() => {
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (!isSpecificUsersAndTeams) {
      return 0;
    }
    return Object.keys(updatedUsers).length;
  }, [updatedUsers, installPermissionAccessState]);
  const updatedTeamsCount = useMemo(() => {
    const isSpecificUsersAndTeams = installPermissionAccessState === 'SPECIFIC_USERS_AND_TEAMS';
    if (!isSpecificUsersAndTeams) {
      return 0;
    }
    return Object.keys(updatedTeams).length;
  }, [updatedTeams, installPermissionAccessState]);
  const hasChanges = useMemo(() => {
    const stateChanged = installPermissionAccessState !== initialInstallPermissionAccessState;
    const usersChanged = updatedUsersCount > 0;
    const teamsChanged = updatedTeamsCount > 0;
    return stateChanged || usersChanged || teamsChanged;
  }, [installPermissionAccessState, initialInstallPermissionAccessState, updatedUsersCount, updatedTeamsCount]);
  const value = useMemo(() => ({
    updatedUsers,
    setUpdatedUsers,
    updatedUsersCount,
    updatedTeams,
    setUpdatedTeams,
    updatedTeamsCount,
    hasChanges,
    initialUserIds,
    initialTeamIds,
    installPermissionAccessState,
    initialInstallPermissionAccessState,
    setInstallPermissionAccessState,
    getSelectedEntityTypeAndIds,
    commitUserUpdates,
    commitTeamUpdates,
    resetUpdatedUsers
  }), [updatedUsers, updatedUsersCount, updatedTeams, updatedTeamsCount, hasChanges, initialUserIds, initialTeamIds, installPermissionAccessState, initialInstallPermissionAccessState, getSelectedEntityTypeAndIds, commitUserUpdates, commitTeamUpdates, resetUpdatedUsers]);
  return /*#__PURE__*/_jsx(InstallPermissionsContext.Provider, {
    value: value,
    children: children
  });
}
export function useInstallPermissions() {
  const context = useContext(InstallPermissionsContext);
  if (!context) {
    throw new Error('useInstallPermissions must be used within InstallPermissionsProvider');
  }
  return context;
}